import {ChangeDetectionStrategy, Component, EventEmitter, inject, Input, OnInit, Output} from '@angular/core';
import {ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {TypeAdmin, User} from '../../../_domains/UITypes';
import {catchError, skip, switchMap} from 'rxjs/operators';
import {DialogModule} from "primeng/dialog";
import {ShowErrorModule} from "../../show-error/show-error.module";
import {PasswordModule} from "primeng/password";
import {ClipboardModule} from "ngx-clipboard";
import {InputTextModule} from "primeng/inputtext";
import {ButtonModule} from "primeng/button";
import {NgIf} from "@angular/common";
import {InputTextareaModule} from "primeng/inputtextarea";
import {CheckboxModule} from "primeng/checkbox";
import {RadioButtonModule} from "primeng/radiobutton";
import {DropdownModule} from "primeng/dropdown";
import {DataAdminService} from "../_services/data-admin.service";
import {AppMessageService} from "../../../_services/app-message.service";
import {of, Subject} from "rxjs";
import {HttpErrorResponse} from "@angular/common/http";
import {CustomValidators} from "../../../_directives/custom.validator";
import {AuthService} from "../../../_services/auth.service";

@Component({
  selector: 'app-admin-user-edit',
  templateUrl: './user-edit.component.html',
  standalone: true,
  imports: [
    DialogModule,
    ReactiveFormsModule,
    ShowErrorModule,
    PasswordModule,
    ClipboardModule,
    InputTextModule,
    ButtonModule,
    NgIf,
    InputTextareaModule,
    CheckboxModule,
    RadioButtonModule,
    DropdownModule
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UserEditComponent implements OnInit {
  @Input() value: User;
  @Input() mode: string;
  @Input() loading = false;
  @Output() visibleChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() saved: EventEmitter<any> = new EventEmitter<any>();
  @Output() showProjects: EventEmitter<void> = new EventEmitter<void>();
  private dataService = inject(DataAdminService);
  private messageService = inject(AppMessageService);
  private authService = inject(AuthService);
  private filterInstitutions$: Subject<{
    dictName: string,
    valueField?: string,
    parentDictName?: string,
    parentValueField?: string,
    parentValue?: string,
    cancel?: boolean
  }>;
  readonly form: UntypedFormGroup;
  private _visible = false;
  cmpVisible = false;
  countries!: any[];
  institutionNames!: any[];

  constructor(private fb: UntypedFormBuilder) {
    this.form = fb.group({
      id: {value: '', disabled: true},
      missionCode: [null, [Validators.required, Validators.maxLength(12), CustomValidators.notBlank()]],
      country: [null, [Validators.required, Validators.maxLength(64)]],
      city: [null, [Validators.required, Validators.maxLength(64), CustomValidators.notBlank()]],
      institutionType: [null, Validators.required],
      institutionName: [null, Validators.required],
      keycloakId: {value: '', disabled: true},
      firstName: [null, [Validators.required, Validators.maxLength(256), Validators.pattern('^[^\\s]+(\\s.*)?$')]],
      lastName: [null, [Validators.required, Validators.maxLength(256), Validators.pattern('^[^\\s]+(\\s.*)?$')]],
      psw: '',
      email: [null, [Validators.required, Validators.maxLength(96)]],
      phone: [null, Validators.maxLength(256)],
      isAdmin: [0],
      enabled: [null],
      sendActivationEmail: null,
      accessTokenValidity: ''
    });
    if (!this.authService.isAdmin) {
      const {missionCode, country, city} = this.form.controls;
      [missionCode, country, city].forEach(v => v.disable());
    }
  }

  ngOnInit() {
    this.loadCountries();
    this.initObservables();
    this.formValueChanges();
  }

  @Input() get visible(): boolean {
    return this._visible;
  }

  set visible(value: boolean) {
    this._visible = value;
    if (this._visible && !this.cmpVisible) {
      this.initForm();
      this.cmpVisible = true;
    } else {
      this.cmpVisible = false;
    }
  }

  save(value: any) {
    this.saved.emit(value);
  }

  hide() {
    this.cmpVisible = false;
    this.visibleChange.emit(false);
  }

  showUserProjects() {
    this.showProjects.emit();
  }

  private initForm() {
    this.form.reset();
    const isEdit = this.value?.id?.length > 0 && this.mode === 'EDIT';
    if (!isEdit) {
      const user = this.authService.user;
      this.form.patchValue({
        missionCode: user.missionCode,
        country: user.country,
        city: user.city,
        isAdmin: TypeAdmin.NONE,
        enabled: true,
        sendActivationEmail: true
      });
    } else {
      this.form.patchValue({
        id: this.value.id,
        missionCode: this.value.missionCode,
        country: this.value.country,
        city: this.value.city,
        institutionType: this.value.institutionType,
        institutionName: this.value.institutionName,
        keycloakId: this.value.keycloakId,
        firstName: this.value.firstName,
        lastName: this.value.lastName,
        enabled: this.value.accountLocked == false,
        sendActivationEmail: false,
        email: this.value.email,
        phone: this.value.phone,
        isAdmin: this.value.typeAdmin,
        accessTokenValidity: this.value.accessTokenValidity
      });
    }
  }

  private loadCountries() {
    this.dataService.getPublicDictData('CountryList')
      .subscribe({
        next: (data) => this.countries = data,
        error: (error) => this.messageService.showMessage(error, 'Error receiving data')
      })
  }

  private formValueChanges() {
    this.form.get('isAdmin').valueChanges
      .pipe(skip(1))
      .subscribe(val => {
          if (val === TypeAdmin.API) {
            this.form.get('psw').setValidators(this.value?.typeAdmin === TypeAdmin.API ?
              [Validators.minLength(3)] : [Validators.required, Validators.minLength(3)]);
          } else {
            this.form.get('psw').setValidators(null);
          }
          this.form.get('psw').setValue(null);
          this.form.get('accessTokenValidity').setValidators(val === TypeAdmin.API ?
            [Validators.required, Validators.min(1), Validators.max(9999), Validators.pattern('^\\d+$')] : null);
          this.form.get('accessTokenValidity').setValue(7);
        }
      );

    const {country, institutionType} = this.form.controls;
    [country, institutionType].forEach(v => v.valueChanges
      .pipe(skip(1))
      .subscribe(() => {
        const countryVal = country.value, institutionTypeVal = institutionType.value;
        const institutionName = this.form.get('institutionName');
        institutionName.setValue(null);
        if (!countryVal?.length || !institutionTypeVal?.length) {
          this.institutionNames = [];
        } else if (institutionTypeVal === 'ResearchAssistant') {
          this.institutionNames = [{value: 'Research Assistant', label: 'Research Assistant'}];
          institutionName.setValue('Research Assistant');
        } else {
          this.institutionNames = [];
          this.filterInstitutions$.next({
            dictName: institutionTypeVal,
            parentDictName: 'CountryList',
            parentValue: countryVal
          });
        }
      }));
  }

  private initObservables() {
    this.filterInstitutions$ = new Subject();
    this.filterInstitutions$.asObservable()
      .pipe(
        switchMap((v) => {
          if (v.cancel) {
            return of('');
          }
          return this.dataService.getPublicDictData(v.dictName, v.valueField, v.parentDictName, v.parentValueField, v.parentValue)
            .pipe(catchError((err) => this.handleError(err)));
        })
      ).subscribe({
      next: (data) => {
        if (typeof data !== 'string') {
          this.institutionNames = data;
        }
      },
      error: (error) => this.messageService.showMessage(error, 'Error receiving data')
    });
  }

  private handleError(error: HttpErrorResponse) {
    this.messageService.showMessage(error, 'Error receiving data')
    return of('ERROR');
  }

  get saveButtonDisabled(): boolean {
    return this.loading || !this.form.valid;
  }

  get cancelButtonDisabled(): boolean {
    return this.loading;
  }

  get userIdVisible(): boolean {
    return this.value?.id?.length > 0;
  }
}
